import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Trash2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDateTimeShort } from '@/lib/dates';
import {
  addSalesStaff,
  attachUnmatchedPayment,
  fetchUnmatchedPayments,
  adminCancelSale,
  adminClearHold,
  fetchSales,
  fetchSalesSettings,
  removeSalesStaff,
  type SalesSettings,
  saveSalesSettings,
  setAllHolds,
  setStaffHold,
  adminIssueClient,
  adminPinClient,
  raiseAdminIssue,
  resetSalesPin,
} from '@/api/admin-sales';
import { ISSUE_RAISABLE } from '@/api/sale-issues';
import { useIsSuperAdmin } from '@/api/hooks';
import { SalesDeskView } from '@/components/admin/sales/SalesDeskView';
import { NoteDialog, type NoteRequest } from '@/components/admin/sales/NoteDialog';
import { IssuesTab } from '@/components/admin/sales/IssuesTab';
import { parseSalesSearch, type SalesNav, type SalesSearch, useSalesTabs } from '@/components/admin/sales/sales-nav';
import { AlfieLoadingPanel } from '@/components/shared/AlfieLoadingPanel';
import { SalesPinButton } from '@/components/admin/sales/SalesPinButton';

export const Route = createFileRoute('/admin/sales')({
  component: SalesRoute,
  validateSearch: (search: Record<string, unknown>): SalesSearch => parseSalesSearch(search),
});

/**
 * Super admins get the full Sales admin page (admin-sales). Everyone else the admin shell
 * lets in here is a salesperson, and gets their own view (sales-desk): the admin page's
 * queries only ever mount for a super admin.
 */
function SalesRoute() {
  const { isSuperAdmin, isLoading } = useIsSuperAdmin();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const nav: SalesNav = { ...search, navigate: (next) => void navigate({ search: next, replace: true }) };
  if (isLoading) return <Skeleton className="m-6 h-24" />;
  return isSuperAdmin ? <SalesAdminPage nav={nav} /> : <SalesDeskView nav={nav} />;
}

const SETTINGS_KEY = ['admin', 'sales-settings'];
const SALES_KEY = ['admin', 'sales-list'];
const UNMATCHED_KEY = ['admin', 'sales-unmatched'];
const ADMIN_TABS = ['sales', 'people', 'settings', 'unconnected', 'issues'] as const;
// Loader lines: each names what the tab's list is doing while it loads.
const SALES_LINES = ['Collating sales data', 'Checking which links have been paid', 'Lining up live and recent sales'] as const;
const PEOPLE_LINES = ['Loading your salespeople', 'Reading each hold'] as const;
const SETTINGS_LINES = ['Reading the sales settings'] as const;
const UNCONNECTED_LINES = ['Reading GHL payments that have no account yet'] as const;
type ListStatus = 'live' | 'paid' | 'expired' | 'superseded' | 'cancelled';
const LIST_LABELS: Record<ListStatus, string> = {
  live: 'Live',
  paid: 'Paid',
  expired: 'Expired',
  superseded: 'Replaced',
  cancelled: 'Cancelled',
};

const SETTING_FIELDS: Array<{ key: keyof SalesSettings; label: string; help: string }> = [
  { key: 'standard_hold_hours', label: 'Standard hold (hours)', help: 'For salespeople without their own hold.' },
  { key: 'sale_link_life_days', label: 'Sale link works for (days)', help: 'The customer’s “Finish joining” link.' },
  { key: 'dormant_delete_days', label: 'Delete never-used accounts after (days)', help: 'Accounts made by Create account, never signed in, never paid.' },
  { key: 'signup_link_life_days', label: 'Ad link works for (days)', help: 'The free-course email link.' },
  { key: 'signup_global_cap_per_hour', label: 'Ad emails per hour, at most', help: 'Beyond this nothing is sent until the hour turns.' },
  { key: 'alert_signup_emails_per_hour', label: 'Slack when ad emails per hour pass', help: '' },
  { key: 'alert_ad_accounts_per_day', label: 'Slack when ad accounts per day pass', help: '' },
  { key: 'alert_signup_refusals_per_hour', label: 'Slack when refused ad requests per hour pass', help: '' },
];

function SalesAdminPage({ nav }: { nav: SalesNav }) {
  const queryClient = useQueryClient();
  const { tab, issueId, setTab, setIssue } = useSalesTabs(nav, ADMIN_TABS);
  const settingsQuery = useQuery({ queryKey: SETTINGS_KEY, queryFn: fetchSalesSettings, refetchOnWindowFocus: false });
  const [listStatus, setListStatus] = useState<ListStatus>('live');
  const salesQuery = useQuery({
    queryKey: [...SALES_KEY, listStatus],
    queryFn: () => fetchSales(listStatus),
    // The Live list shows a sale complete without a reload: every 30 seconds, and on return
    // to the tab. Paid sales stay on Live for 24 hours with a Paid badge (admin-sales).
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const unmatchedQuery = useQuery({ queryKey: UNMATCHED_KEY, queryFn: fetchUnmatchedPayments, refetchOnWindowFocus: false });
  // Same key as the Issues tab's open list, so the count and the tab share one fetch.
  const openIssues = useQuery({
    queryKey: [...adminIssueClient.key, 'issues', 'open'],
    queryFn: () => adminIssueClient.list('open'),
    refetchOnWindowFocus: false,
  });
  const openIssueCount = openIssues.data?.issues.length ?? 0;
  const [noteRequest, setNoteRequest] = useState<NoteRequest | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    void queryClient.invalidateQueries({ queryKey: SALES_KEY });
    void queryClient.invalidateQueries({ queryKey: UNMATCHED_KEY });
    void queryClient.invalidateQueries({ queryKey: adminIssueClient.key });
  };
  const onError = (e: unknown) => toast.error(e instanceof Error ? e.message : 'Something went wrong');

  const [newEmail, setNewEmail] = useState('');
  const [allHours, setAllHoursValue] = useState('');
  const [draft, setDraft] = useState<Partial<Record<keyof SalesSettings, string>>>({});

  const add = useMutation({
    mutationFn: () => addSalesStaff(newEmail.trim()),
    onSuccess: () => {
      setNewEmail('');
      toast.success('Salesperson added');
      refresh();
    },
    onError,
  });
  const remove = useMutation({ mutationFn: removeSalesStaff, onSuccess: refresh, onError });
  const resetPin = useMutation({
    mutationFn: ({ userId, note }: { userId: string; note: string }) => resetSalesPin(userId, note || undefined),
    onSuccess: () => {
      toast.success('PIN reset. They set a new one on the Sales page in Alfie.');
      refresh();
    },
    onError,
  });
  const hold = useMutation({
    mutationFn: ({ userId, hours }: { userId: string; hours: number | null }) => setStaffHold(userId, hours),
    onSuccess: refresh,
    onError,
  });
  const all = useMutation({
    mutationFn: (h: number) => setAllHolds(h),
    onSuccess: () => {
      toast.success('Every salesperson and the standard updated');
      setAllHoursValue('');
      refresh();
    },
    onError,
  });
  const save = useMutation({
    mutationFn: (fields: Partial<SalesSettings>) => saveSalesSettings(fields),
    onSuccess: () => {
      toast.success('Settings saved');
      setDraft({});
      refresh();
    },
    onError,
  });
  const attach = useMutation({
    mutationFn: attachUnmatchedPayment,
    onSuccess: () => {
      toast.success('Payment attached. The customer has access.');
      refresh();
    },
    onError,
  });
  const raise = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => raiseAdminIssue(id, note),
    onSuccess: () => {
      toast.success('Issue raised. It is in the Issues tab.');
      refresh();
    },
    onError,
  });
  const clear = useMutation({
    mutationFn: ({ id, reason, cancel }: { id: string; reason: string; cancel: boolean }) =>
      cancel ? adminCancelSale(id, reason) : adminClearHold(id, reason),
    onSuccess: refresh,
    onError,
  });

  function askReason(id: string, cancel: boolean) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(cancel ? 'Why is this sale being cancelled?' : 'Why is this hold being cleared?');
    if (reason && reason.trim()) clear.mutate({ id, reason: reason.trim(), cancel });
  }

  const settings = settingsQuery.data?.settings;
  const staff = settingsQuery.data?.staff ?? [];
  const allHoursNumber = Number(allHours);

  return (
    // The admin shell wraps <Outlet /> in a non-scrolling box, so the page owns its scroller
    // (matches admin/surveys.tsx).
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-7xl space-y-4 p-4 pb-[calc(2rem+env(safe-area-inset-bottom))] sm:p-6 sm:pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-foreground">Sales</h1>
          <p className="text-xs text-muted-foreground">Tracking and settings</p>
        </div>
        <SalesPinButton client={adminPinClient} queryKey={['admin', 'sales-pin']} />
      </div>

      {settingsQuery.isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{(settingsQuery.error as Error).message}</AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="people">People</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="unconnected">Unconnected GHL payments</TabsTrigger>
          <TabsTrigger value="issues">Issues{openIssueCount > 0 ? ` (${openIssueCount})` : ''}</TabsTrigger>
        </TabsList>

      <TabsContent value="sales" className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {(Object.keys(LIST_LABELS) as ListStatus[]).map((k) => (
            <Button
              key={k}
              size="sm"
              variant={listStatus === k ? 'default' : 'outline'}
              onClick={() => setListStatus(k)}
            >
              {LIST_LABELS[k]}
            </Button>
          ))}
        </div>
        {salesQuery.isLoading ? (
          <AlfieLoadingPanel title="Loading sales" lines={SALES_LINES} />
        ) : (salesQuery.data?.sales ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">No {LIST_LABELS[listStatus].toLowerCase()} sales.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2 font-medium">Customer</th>
                <th className="py-2 font-medium">Plan</th>
                <th className="py-2 font-medium">Salesperson</th>
                <th className="py-2 font-medium">Held until</th>
                <th className="py-2 font-medium">Link</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {(salesQuery.data?.sales ?? []).map((s) => (
                <tr key={s.id} className="border-t border-border align-top">
                  <td className="py-2">
                    <div className="font-medium">{s.full_name}</div>
                    <div className="text-xs text-muted-foreground">{s.email_norm} · {s.business_name}</div>
                  </td>
                  <td className="py-2">
                    {s.tier_code}
                    {s.promo_code && <div className="text-xs text-muted-foreground">promo {s.promo_code}</div>}
                    {s.status === 'paying' && <Badge className="ml-1">paying</Badge>}
                    {s.status === 'paid' && (
                      <Badge className="ml-1 bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]">Paid</Badge>
                    )}
                    {s.paid_at && (
                      <div className="text-xs text-muted-foreground">
                        paid {s.payment_source === 'ghl_form' ? 'on GHL' : 'in Alfie'} {formatDateTimeShort(s.paid_at)}
                      </div>
                    )}
                  </td>
                  <td className="py-2">{s.sold_by_name ?? s.sold_by_email}</td>
                  <td className="py-2">{formatDateTimeShort(s.held_until)}</td>
                  <td className="py-2">
                    {s.link_sent_at ? `sent ×${s.link_send_count}` : 'not sent'}
                  </td>
                  <td className="space-x-1 whitespace-nowrap py-2 text-right">
                    {(s.status === 'open' || s.status === 'paying') && (
                      <>
                    <Button variant="outline" size="sm" onClick={() => askReason(s.id, false)}>
                      Clear hold
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => askReason(s.id, true)}>
                      Cancel sale
                    </Button>
                      </>
                    )}
                    {ISSUE_RAISABLE.has(s.status) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={raise.isPending}
                        onClick={() =>
                          setNoteRequest({
                            title: 'Raise an issue',
                            description: `What might be wrong with the sale for ${s.email_norm}?`,
                            confirmLabel: 'Raise issue',
                            required: true,
                            onConfirm: (note) => raise.mutate({ id: s.id, note }),
                          })}
                      >
                        Raise an issue
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TabsContent>

      <TabsContent value="people" className="space-y-3">
        <div className="flex max-w-xl gap-2">
          <Input
            placeholder="Their Alfie email"
            value={newEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewEmail(e.target.value)}
          />
          <Button onClick={() => add.mutate()} disabled={!newEmail.trim() || add.isPending}>
            <UserPlus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </div>
        {settingsQuery.isLoading ? (
          <AlfieLoadingPanel title="Loading people" lines={PEOPLE_LINES} />
        ) : staff.length === 0 ? (
          <p className="text-sm text-muted-foreground">No salespeople yet.</p>
        ) : (
          <table className="w-full max-w-3xl text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2 font-medium">Salesperson</th>
                <th className="py-2 font-medium">Hold (hours)</th>
                <th className="py-2 font-medium">Sales page PIN</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.userId} className="border-t border-border">
                  <td className="py-2">
                    <div className="font-medium">{s.name ?? s.email}</div>
                    <div className="text-xs text-muted-foreground">{s.email}</div>
                  </td>
                  <td className="py-2">
                    <Input
                      className="w-28"
                      type="number"
                      min={1}
                      max={720}
                      defaultValue={s.holdHours ?? ''}
                      placeholder={settings ? `${settings.standard_hold_hours} (standard)` : ''}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                        const v = e.target.value.trim();
                        const next = v === '' ? null : Number(v);
                        if (next === s.holdHours) return;
                        if (next !== null && (!Number.isInteger(next) || next < 1 || next > 720)) {
                          toast.error('Use 1 to 720 hours');
                          return;
                        }
                        hold.mutate({ userId: s.userId, hours: next });
                      }}
                    />
                  </td>
                  <td className="py-2">
                    {s.pinSetAt ? (
                      <span className="text-xs text-muted-foreground">set {formatDateTimeShort(s.pinSetAt)}</span>
                    ) : (
                      <span className="text-xs text-[hsl(var(--warning))]">not set</span>
                    )}
                    {s.pinSetAt && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-1"
                        disabled={resetPin.isPending}
                        onClick={() =>
                          setNoteRequest({
                            title: 'Reset this PIN?',
                            description: `${s.name ?? s.email} can't use the sales page until they set a new PIN in Alfie. Optional: say why (it goes to Slack).`,
                            confirmLabel: 'Reset PIN',
                            required: false,
                            onConfirm: (note) => resetPin.mutate({ userId: s.userId, note }),
                          })}
                      >
                        Reset PIN
                      </Button>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => remove.mutate(s.userId)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex max-w-xl items-center gap-2 pt-2">
          <Input
            className="w-28"
            type="number"
            min={1}
            max={720}
            placeholder="Hours"
            value={allHours}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAllHoursValue(e.target.value)}
          />
          <Button
            variant="outline"
            disabled={!Number.isInteger(allHoursNumber) || allHoursNumber < 1 || allHoursNumber > 720 || all.isPending}
            onClick={() => all.mutate(allHoursNumber)}
          >
            Set every salesperson and the standard
          </Button>
        </div>
      </TabsContent>

      <TabsContent value="settings" className="space-y-3">
        {settingsQuery.isLoading && <AlfieLoadingPanel title="Loading settings" lines={SETTINGS_LINES} />}
        {settings && (
          <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
            {SETTING_FIELDS.map((f) => (
              <label key={f.key} className="space-y-1 text-sm">
                <span className="font-medium">{f.label}</span>
                <Input
                  type="number"
                  value={draft[f.key] ?? String(settings[f.key])}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                />
                {f.help && <span className="block text-xs text-muted-foreground">{f.help}</span>}
              </label>
            ))}
          </div>
        )}
        <Button
          disabled={Object.keys(draft).length === 0 || save.isPending}
          onClick={() => {
            const fields: Partial<SalesSettings> = {};
            for (const [k, v] of Object.entries(draft)) {
              const n = Number(v);
              if (!Number.isInteger(n)) {
                toast.error('Whole numbers only');
                return;
              }
              fields[k as keyof SalesSettings] = n;
            }
            save.mutate(fields);
          }}
        >
          Save settings
        </Button>
      </TabsContent>

      <TabsContent value="unconnected" className="space-y-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Paid on GHL's form before Create account was pressed. Usually these attach by themselves once
          Create account is pressed. Use Attach when they have not.
        </p>
        {unmatchedQuery.isLoading ? (
          <AlfieLoadingPanel title="Loading payments" lines={UNCONNECTED_LINES} />
        ) : (unmatchedQuery.data?.unmatched ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">None.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-2 font-medium">Email</th>
                <th className="py-2 font-medium">Plan</th>
                <th className="py-2 font-medium">Paid</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {(unmatchedQuery.data?.unmatched ?? []).map((u) => (
                <tr key={u.id} className="border-t border-border">
                  <td className="py-2">{u.email_norm ?? 'unknown'}</td>
                  <td className="py-2">{u.tier_code ?? ''}</td>
                  <td className="py-2">{formatDateTimeShort(u.created_at)}</td>
                  <td className="py-2 text-right">
                    <Button size="sm" variant="outline" disabled={attach.isPending} onClick={() => attach.mutate(u.id)}>
                      Attach
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </TabsContent>

      <TabsContent value="issues">
        <IssuesTab client={adminIssueClient} openIssueId={issueId} onOpenIssue={setIssue} />
      </TabsContent>
      </Tabs>
      </div>
      <NoteDialog request={noteRequest} onClose={() => setNoteRequest(null)} />
    </div>
  );
}
